import { Component, OnDestroy, OnInit } from '@angular/core';
import { AmplitudeService } from '@inyova/inyova-shared';
import { Customer, CustomerAccount } from '@inyova/models';

import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as StrategyActions from '@strategy/strategy.actions';
import * as fromAppRoot from '@app/app.reducers';
import * as fromStrategy from '@strategy/reducers';

import { INVESTMENT_LIMITS, INVESTMENT_CHIP_OPTIONS } from '@app/app.constants';
import { TrackingService } from '@shared/services/tracking.service';

@Component({
  selector: 'app-setup-investment',
  templateUrl: './setup-investment.component.html',
  styleUrls: ['./setup-investment.component.scss'],
  standalone: false
})
export class SetupInvestmentComponent implements OnDestroy, OnInit {
  protected readonly onDestroy$ = new Subject<void>();
  customer: Customer;
  currentAccount: CustomerAccount;
  error = false;
  initialOptions = INVESTMENT_CHIP_OPTIONS.initial['ch'];
  isLoading: boolean;
  minimumInitial = INVESTMENT_LIMITS.initial['ch'];
  minimumMonthly = INVESTMENT_LIMITS.monthly['ch'];
  maxInvestValue = INVESTMENT_LIMITS.max;
  monthlyOptions = INVESTMENT_CHIP_OPTIONS.monthly['ch'];
  initialInvestment: number;
  monthlyInvestment: number;
  originalValues: {
    initialInvestment: number;
    monthlyInvestment: number;
  };

  constructor(
    private store: Store,
    private trackingService: TrackingService,
    private amplitudeService: AmplitudeService
  ) {
    this.store
      .pipe(select(fromAppRoot.selectCustomerAndCurrentInvestAccount), takeUntil(this.onDestroy$))
      .subscribe(({ customer, currentAccountDetail }) => {
        this.customer = customer;
        this.currentAccount = currentAccountDetail;
        this.initialOptions = INVESTMENT_CHIP_OPTIONS.initial[this.customer.app_location];
        this.monthlyOptions = INVESTMENT_CHIP_OPTIONS.monthly[this.customer.app_location];
        this.minimumMonthly = INVESTMENT_LIMITS.monthly[this.customer.app_location];
        this.minimumInitial = INVESTMENT_LIMITS.initial[this.customer.app_location];
      });
    this.store.pipe(select(fromStrategy.selectInvestmentLoader), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.isLoading = value;
    });
    this.store.pipe(select(fromStrategy.selectCustomerInvestmentData), takeUntil(this.onDestroy$)).subscribe((value) => {
      this.initialInvestment = this.initialInvestment ?? value.total;
      this.monthlyInvestment = this.monthlyInvestment ?? value.monthly_investment;
      this.originalValues = {
        initialInvestment: value.total,
        monthlyInvestment: value.monthly_investment
      };
    });
  }

  ngOnInit(): void {
    this.trackingService.trackView('/investment-profile');
  }

  checkNumberValidChars(event: KeyboardEvent): void {
    const invalidChars = ['-', '+', 'e', 'E', '.', ','];
    if (event) {
      if (invalidChars.includes(event.key)) {
        event.preventDefault();
        return;
      }
    }

    const inputValue = Number((event.target as HTMLInputElement).value);

    if (inputValue >= this.maxInvestValue) {
      const key = Number(event.key);
      if (!isNaN(key) || event.key === ' ') {
        event.preventDefault();
      }
    }
  }

  updateInvestment(type: string, value: string | number): void {
    if (type === 'initial') {
      this.initialInvestment = value ? Number(value.toString().replace(/[ ,.]/g, '')) : null;
      if (this.initialInvestment >= this.maxInvestValue) {
        this.initialInvestment = this.maxInvestValue;
      }
      this.trackingService.trackActivity(`[Button] Initial ${value}`);
    } else {
      this.monthlyInvestment = value ? Number(value.toString().replace(/[ ,.]/g, '')) : null;
      if (this.monthlyInvestment >= this.maxInvestValue) {
        this.monthlyInvestment = this.maxInvestValue;
      }
      this.trackingService.trackActivity(`[Button] Monthly ${value}`);
    }

    this.error = this.isUnderMinimum();
  }

  isUnderMinimum(): boolean {
    if (this.initialInvestment < this.minimumInitial && this.monthlyInvestment < this.minimumMonthly) {
      return true;
    }
    return false;
  }

  otherInvestment(type: string, value: number): boolean {
    if (type === 'monthly') {
      return !this.monthlyOptions.includes(value);
    }
    return !this.initialOptions.includes(value);
  }

  evaluate(type: string, value: string): void {
    if (value.length === 0) {
      if (type === 'initial') {
        this.initialInvestment = 0;
      } else {
        this.monthlyInvestment = 0;
      }
    }
  }

  trackTooltip(name: string) {
    this.amplitudeService.trackEvent('Tooltip Info Consulted', {
      tooltip_name: name,
      screen: 'DE setup risk',
      consultation_method: 'hover'
    });
  }

  submitInvestment(): void {
    const monthly = this.monthlyInvestment ? this.monthlyInvestment : 0;
    const initial = this.initialInvestment ? this.initialInvestment : 0;
    const investmentPlan = {
      initial_investment: initial,
      monthly_investment: monthly
    };
    if (!this.error) {
      this.store.dispatch(StrategyActions.updateInvestmentPlan(investmentPlan));
    }
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }
}
